import ChatbotBaseController from './chatbot_base_controller'

/**
 * User-facing support chatbot controller.
 * Extends ChatbotBaseController with modal-specific behavior and UI.
 */
export default class extends ChatbotBaseController {
  static targets = [
    ...ChatbotBaseController.targets,
    'triggerButton',
    'modal',
    'modalBody',
    'conversationHistory',
    'historyList',
    'feedbackDialog',
    'feedbackComment',
    'feedbackCommentLabel',
    'feedbackCommentInput',
    'feedbackSubmitButton',
  ]

  static values = {
    helpMessage: {
      type: String,
      default:
        "Hi! I'm your virtual assistant, available to answer any questions 24/7. What can I help you with today?",
    },
    messageReviewsPath: String,
  }

  /**
   * Custom initialization for modal-specific behavior
   */
  afterConnect() {
    // Override CSS class names to match user support modal styling
    this.cssClasses = {
      messageContainer: '', // No wrapper container for modal
      messageBubble: 'message-bubble',
      userMessage: 'user-message',
      assistantMessage: '', // No separate assistant class
      messageText: '', // No separate text element
      sourcesPillContainer: 'sources-pill-container',
      sourcesPill: 'sources-pill',
      sourcesPopup: 'sources-popup',
      sourcesPopupHeader: 'sources-popup-header',
      sourcesPopupList: 'sources-popup-list',
      sourceItem: 'source-item',
      sourceLink: 'source-link',
      sourceTitle: 'source-title',
      sourceMeta: 'source-meta',
    }

    // Feedback state
    this.lastAssistantMessageId = null
    this.receivedNewResponse = false
    this.selectedRating = 0

    // For anonymous users, seed conversation state so the first sendMessage
    // carries the token to the server for conversation lookup.
    if (this.anonymousValue) {
      const token = this._getOrCreateAnonymousToken()
      this.currentConversation = { conversation_token: token }

      // Before any form submit (sign-in, sign-up), flush tracked conversation
      // tokens to a short-lived cookie so the server can claim them after auth.
      this._boundFlushTokens = this._flushAnonymousTokensToCookie.bind(this)
      document.addEventListener('submit', this._boundFlushTokens)
    } else {
      sessionStorage.removeItem('anonymousConversationTokens')
    }

    // Deep link from email CTA takes priority over session restore
    const params = new URLSearchParams(window.location.search)
    if (params.has('open_chat')) {
      // Remove the query parameter from the URL
      params.delete('open_chat')
      const cleanUrl = `${window.location.pathname}${params.size ? '?' + params : ''}`
      window.history.replaceState({}, '', cleanUrl)

      // Remove any previous conversation from session storage
      sessionStorage.removeItem(this._sessionStorageKey)

      // Open the modal and start the chat
      this.openModal()
      this.startChat()
    } else {
      this.restoreFromSession()
    }
  }

  disconnect() {
    super.disconnect()
    if (this._boundFlushTokens) {
      document.removeEventListener('submit', this._boundFlushTokens)
    }
  }

  // After two seconds the AI support button will collapse
  triggerButtonTargetConnected() {
    setTimeout(() => {
      this.triggerButtonTarget.classList.add('user-support-collapsing')
    }, 2000)
  }

  openModal() {
    this.modalTarget.classList.add('modal-open')
    document.body.classList.add('modal-active')
    this._syncConversationStorage(true)
    this.showChatView()
  }

  closeModal() {
    if (this.hasFeedbackDialogTarget && this.receivedNewResponse) {
      if (this._isFeedbackDialogVisible()) {
        this.skipFeedback()
      } else {
        this._showFeedbackDialog()
      }
      return
    }

    this._hideFeedbackDialog()
    this.modalTarget.classList.remove('modal-open')
    document.body.classList.remove('modal-active')
    this._syncConversationStorage(false)
  }

  closeSupportUI() {
    this.closeModal()
  }

  showChatView() {
    this.greetingTarget.style.display = 'block'
    this.greetingTarget.textContent = this.helpMessageValue
    this.chatInterfaceTarget.style.display = 'block'
    this.chatBoxTarget.style.display = 'block'
    this.chatInputContainerTarget.style.display = 'flex'

    if (this.hasConversationHistoryTarget) {
      this.conversationHistoryTarget.style.display = 'none'
    }

    this.updateChatBoxMargin()
  }

  /**
   * Update chat-box margin to match input container height
   * (similar to autoResizeTextarea but for initial load)
   */
  updateChatBoxMargin() {
    // Wait for next frame so the container is visible and has its full height
    requestAnimationFrame(() => {
      const chatBox = this.chatBoxTarget
      const inputContainer = this.chatInputContainerTarget
      chatBox.style.marginBottom = inputContainer.offsetHeight + 'px'
    })
  }

  openOldConversations() {
    this._resetFeedbackState()

    // Hide other views
    this.chatInterfaceTarget.style.display = 'none'
    this.chatInputContainerTarget.style.display = 'none'
    this.greetingTarget.style.display = 'none'
    this.chatBoxTarget.style.display = 'none'

    // Show conversation history
    if (this.hasConversationHistoryTarget) {
      this.conversationHistoryTarget.style.display = 'block'
    }

    // Reset to loading state
    this.historyListTarget.innerHTML = `
      <div class="loading-history">
        <span class="fas fa-spinner fa-spin"></span>
        Loading conversations...
      </div>
    `

    // Clear current conversation token so page refresh won't auto-load a conversation
    sessionStorage.removeItem(this._sessionStorageKey)

    // Load conversation history
    this.loadConversationHistory()
  }

  // Close modal when clicking outside the content area
  modalBackdropClick(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }

  /**
   * Override: Render the conversation history list in modal format
   */
  renderConversationHistory(conversations) {
    if (conversations.length === 0) {
      this.historyListTarget.innerHTML = `
        <div class="conversation-notice text-muted">
          <span class="fas fa-comment"></span>
          No previous conversations found.
        </div>
      `
      return
    }

    const conversationItems = conversations
      .map((conversation) => {
        const title = conversation.title || 'Untitled Conversation'

        return `
        <div class="conversation-item"
             data-conversation-id="${conversation.id}"
             data-action="click->user-support#loadConversationFromHistory">
          <div class="conversation-content">
            <div class="conversation-title" role="link" tabindex="0">${this.escapeHtml(title)}</div>
          </div>
          <div class="conversation-arrow">
            <span class="fas fa-chevron-right"></span>
          </div>
        </div>
      `
      })
      .join('')

    this.historyListTarget.innerHTML = conversationItems
  }

  /**
   * Render messages then switch to modal chat view.
   */
  displayLoadedConversation(conversation) {
    super.displayLoadedConversation(conversation)
    this.showChatView()
    this.scrollToBottom()
  }

  /**
   * Override: Show error message in history list
   */
  showConversationError(message) {
    if (this.hasHistoryListTarget) {
      this.historyListTarget.innerHTML = `
        <div class="conversation-notice text-danger">
          <span class="fas fa-exclamation-triangle"></span>
          ${message}
      </div>
    `
    }
  }

  clearChat() {
    super.clearChat()
    this._resetFeedbackState()
  }

  prepareRestore() {
    this.openModal()
  }

  // ========================================
  // ANONYMOUS SESSION
  // ========================================

  extraPayloadData() {
    if (this.anonymousValue) {
      return { url_context: this._urlContext() }
    }
    return {}
  }

  onConversationConfirmed(summary) {
    if (this.anonymousValue) {
      this._trackAnonymousToken(summary.conversation_token)
    }
  }

  _urlContext() {
    const path = window.location.pathname
    if (path.startsWith('/students')) return 'student'
    if (path.startsWith('/alumni')) return 'alumni'
    return 'root'
  }

  _getOrCreateAnonymousToken() {
    return crypto.randomUUID()
  }

  _trackAnonymousToken(token) {
    if (!token) return
    const key = 'anonymousConversationTokens'
    const existing = JSON.parse(sessionStorage.getItem(key) || '[]')
    if (!existing.includes(token)) {
      existing.push(token)
      sessionStorage.setItem(key, JSON.stringify(existing))
    }
  }

  _flushAnonymousTokensToCookie() {
    const tokens = sessionStorage.getItem('anonymousConversationTokens')
    if (!tokens) return
    document.cookie = `anonymous_conversation_tokens=${encodeURIComponent(tokens)}; path=/; max-age=120; SameSite=Lax; Secure`
  }

  // ========================================
  // FEEDBACK
  // ========================================

  onAssistantResponse(messageId) {
    this.lastAssistantMessageId = messageId
    this.receivedNewResponse = true
  }

  selectStar(event) {
    const star = event.currentTarget
    this.selectedRating = parseInt(star.dataset.starValue, 10)

    this.feedbackDialogTarget
      .querySelectorAll('.feedback-star .fa-star')
      .forEach((icon, index) => {
        if (index < this.selectedRating) {
          icon.classList.remove('far')
          icon.classList.add('fas')
        } else {
          icon.classList.remove('fas')
          icon.classList.add('far')
        }
      })

    if (this.hasFeedbackCommentTarget) {
      this.feedbackCommentTarget.style.display = 'block'
    }

    if (this.hasFeedbackCommentLabelTarget) {
      this.feedbackCommentLabelTarget.textContent =
        this._feedbackLabelForRating(this.selectedRating)
    }

    if (this.hasFeedbackSubmitButtonTarget) {
      this.feedbackSubmitButtonTarget.disabled = false
    }

    requestAnimationFrame(() => {
      this.chatBoxTarget.style.marginBottom =
        this.feedbackDialogTarget.offsetHeight + 'px'
    })
  }

  handleFeedbackEnterKey(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.submitFeedback()
    }
  }

  submitFeedback() {
    if (!this.selectedRating || !this.lastAssistantMessageId) return

    fetch(this.messageReviewsPathValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content'),
      },
      body: JSON.stringify({
        ai_message_id: this.lastAssistantMessageId,
        score: this.selectedRating,
        reason: this.hasFeedbackCommentInputTarget
          ? this.feedbackCommentInputTarget.value.trim() || null
          : null,
      }),
    }).catch((error) => console.error('Error submitting feedback:', error))

    this._closeAndReset()
  }

  skipFeedback() {
    this._closeAndReset()
  }

  // ========================================
  // FEEDBACK (PRIVATE)
  // ========================================

  _closeAndReset() {
    this.receivedNewResponse = false
    this.closeModal()
    this.startChat()
  }

  _isFeedbackDialogVisible() {
    return (
      this.hasFeedbackDialogTarget &&
      this.feedbackDialogTarget.style.display === 'flex'
    )
  }

  _showFeedbackDialog() {
    this.chatInputContainerTarget.style.display = 'none'
    this.feedbackDialogTarget.style.display = 'flex'

    requestAnimationFrame(() => {
      this.chatBoxTarget.style.marginBottom =
        this.feedbackDialogTarget.offsetHeight + 'px'
      this.scrollToBottom()
    })
  }

  _hideFeedbackDialog() {
    if (this.hasFeedbackDialogTarget) {
      this.feedbackDialogTarget.style.display = 'none'
    }
  }

  _resetFeedbackState() {
    this.lastAssistantMessageId = null
    this.receivedNewResponse = false
    this.selectedRating = 0

    if (this.hasFeedbackDialogTarget) {
      this.feedbackDialogTarget.style.display = 'none'
      this.feedbackDialogTarget
        .querySelectorAll('.feedback-star .fa-star')
        .forEach((icon) => {
          icon.classList.remove('fas')
          icon.classList.add('far')
        })
    }
    if (this.hasFeedbackCommentTarget) {
      this.feedbackCommentTarget.style.display = 'none'
    }
    if (this.hasFeedbackCommentInputTarget) {
      this.feedbackCommentInputTarget.value = ''
    }
    if (this.hasFeedbackSubmitButtonTarget) {
      this.feedbackSubmitButtonTarget.disabled = true
    }
  }

  _feedbackLabelForRating(rating) {
    if (rating <= 2) return 'What can we do to improve your experience?'
    if (rating <= 3) return 'What could we do better?'
    return "We're glad we could help! Anything else you'd like to share?"
  }
}
